import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  TrendingUp, ShieldCheck, Zap, Bot, Trophy, ArrowRight, Users,
  Activity, LineChart, Sparkles, Layers, DollarSign,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  bronze: "bg-orange-800/70 text-orange-100",
  silver: "bg-slate-400/80 text-slate-900",
  gold: "bg-amber-500/90 text-amber-950",
  platinum: "bg-gradient-to-r from-slate-200 to-slate-400 text-slate-900",
};

const FEATURES = [
  { icon: ShieldCheck, title: "Verified Track Records", body: "Every published bot's P&L is cryptographically signed hourly into a tamper-evident hash chain. Anyone can independently verify a return before copying." },
  { icon: Users, title: "Group Copy Trading", body: "Follow multiple publishers with a target allocation split. Rebalance live — no delete-and-rebuild." },
  { icon: Zap, title: "Real-Time Alerts", body: "Compose AND/OR alert rules on RSI, MACD, EMA crosses, volume, and news sentiment. Toast, push, and email fan-out." },
  { icon: Bot, title: "No-Code Bots", body: "Drag nodes to build strategies. Promote any alert rule to an auto-trading bot with stop-loss + take-profit in a click." },
  { icon: LineChart, title: "Advanced Backtests", body: "Walk-forward + Monte Carlo with slippage + fee modeling. Compare 5 strategies on any range in one click." },
  { icon: Sparkles, title: "AI Coach", body: "Claude Sonnet 5 grounded in Reminiscences, Market Wizards, Trading in the Zone. Ask anywhere, stream anywhere." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [stats, setStats] = useState({ verified: 0, active_bots: 0 });

  useEffect(() => {
    axios.get(`${API}/community/bots?limit=6&sort=score`).then(({ data }) => {
      const rows = data.bots || [];
      setBots(rows);
      setStats({
        verified: rows.filter((b) => b.is_verified).length,
        active_bots: rows.length,
        top_return: Math.max(0, ...rows.map((b) => b.ranking_return_pct || 0)),
      });
    }).catch(() => { /* silent */ });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white overflow-x-hidden" data-testid="landing-page">
      {/* Grain / atmosphere */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.03]" aria-hidden="true"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      {/* Nav */}
      <nav className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/landing" className="flex items-center gap-2 group" data-testid="landing-logo">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-black" />
          </div>
          <div>
            <div className="font-display text-xl font-bold tracking-tight">Aanar</div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 -mt-1">Auto-trading, verified</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-white/70 hover:text-white transition-colors" data-testid="landing-signin">Sign in</Link>
          <Button onClick={() => navigate("/register")} data-testid="landing-cta-nav"
                  className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
            Start free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-32">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono-tab mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {stats.active_bots} verified bots live · +{(stats.top_return || 0).toFixed(1)}% best return · IBKR-native
              </div>
              <div className="flex items-center gap-3 mb-5">
                {/* "A is a price line" mark — mountains + support line */}
                <svg viewBox="0 0 64 48" className="w-14 h-10" aria-hidden="true">
                  <defs>
                    <linearGradient id="hero-a-grad" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#a3e635" />
                    </linearGradient>
                  </defs>
                  <path d="M4 42 L22 8 L34 30 L44 18 L60 42 Z" fill="none"
                        stroke="url(#hero-a-grad)" strokeWidth="3" strokeLinejoin="round" />
                  <line x1="4" y1="42" x2="60" y2="42" stroke="url(#hero-a-grad)"
                        strokeWidth="2" strokeDasharray="2 3" opacity="0.7" />
                </svg>
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                  A is a price line
                </div>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02] mb-6">
                Auto-trading <br />
                <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-lime-300 bg-clip-text text-transparent">
                  you can verify.
                </span>
              </h1>
              <p className="text-lg text-white/60 leading-relaxed max-w-xl mb-6">
                Chart Copilot reads setups in plain English. Bots trade paper or live IBKR
                (1-tap confirm, native MCP). Every published bot's P&L is signed into a
                tamper-evident hash chain — so you can <span className="italic">check</span>,
                not just trust.
              </p>
              {/* Value stack pitch */}
              <div className="mb-8 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="line-through text-white/30 font-mono-tab">$301/mo of tools</span>
                <ArrowRight className="w-3.5 h-3.5 text-white/40" />
                <span className="font-mono-tab text-emerald-300 font-bold">$14/mo</span>
                <span className="text-white/50">· or lock the</span>
                <Link to="/founder" className="text-emerald-400 hover:text-emerald-300 font-semibold underline decoration-dotted underline-offset-2">
                  Founder lifetime deal
                </Link>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate("/register")} data-testid="landing-cta-hero"
                        className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 h-12">
                  Get started free <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={() => document.getElementById("verified")?.scrollIntoView({ behavior: "smooth" })}
                        data-testid="landing-cta-explore"
                        className="rounded-full border-white/20 hover:border-white/40 bg-transparent text-white px-6 h-12">
                  Browse verified bots
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs text-white/40 flex-wrap">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Signed hash chain</span>
                <span className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Real-time streaming</span>
                <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> IBKR MCP native</span>
              </div>
            </motion.div>
          </div>

          {/* Hero visual — floating leaderboard preview */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
                      className="relative">
            <div className="absolute inset-0 bg-emerald-500/20 blur-[100px] rounded-full" />
            <Card className="relative bg-white/5 border-white/10 backdrop-blur-xl rounded-2xl p-6" data-testid="landing-hero-card">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-widest text-white/50">Community Leaderboard</div>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 rounded-full text-[9px]">LIVE</Badge>
              </div>
              <div className="space-y-2">
                {bots.slice(0, 5).map((b, i) => (
                  <div key={b.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors" data-testid={`landing-bot-preview-${i}`}>
                    <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold font-mono-tab">
                      #{i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {b.name}
                        {b.is_verified && <ShieldCheck className="w-3 h-3 text-emerald-400 flex-shrink-0" />}
                      </div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">{b.strategy}</div>
                    </div>
                    {b.tier && (
                      <span className={`text-[8px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${TIER_STYLES[b.tier]}`}>
                        {b.tier}
                      </span>
                    )}
                    <span className={`text-sm font-mono-tab font-bold ${(b.ranking_return_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {b.ranking_return_pct >= 0 ? "+" : ""}{(b.ranking_return_pct || 0).toFixed(1)}%
                    </span>
                  </div>
                ))}
                {bots.length === 0 && Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-11 rounded-lg bg-white/[0.03] animate-pulse" />
                ))}
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="mb-16 max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">Why Aanar</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
            An auto-trading stack you don't have to trust — you can <span className="italic">check</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}>
              <Card className="bg-white/[0.02] border-white/10 hover:border-emerald-500/30 hover:bg-white/[0.04] transition-all rounded-2xl p-6 h-full" data-testid={`landing-feature-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="font-display text-lg font-bold mb-2">{f.title}</div>
                <div className="text-sm text-white/50 leading-relaxed">{f.body}</div>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Verified bots section */}
      <section id="verified" className="relative z-10 max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">Verified now</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
              These bots are backed by <br /><span className="text-emerald-400">signed track records.</span>
            </h2>
          </div>
          <div className="text-sm text-white/50">
            Click any bot to inspect its hash chain →
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bots.length === 0 && (
            <div className="col-span-full text-white/40 py-16 text-center text-sm" data-testid="landing-bots-empty">
              No verified bots yet — be the first to publish.
            </div>
          )}
          {bots.map((b, i) => (
            <Link key={b.id} to={`/bots/${b.id}/postmortem`}
                  className="block group" data-testid={`landing-verified-card-${i}`}>
              <Card className="bg-white/[0.02] border-white/10 group-hover:border-emerald-500/40 group-hover:bg-white/[0.04] transition-all rounded-2xl p-5 h-full">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg flex items-center gap-1.5 truncate">
                      {b.name}
                      {b.is_verified && <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">{b.strategy}</div>
                  </div>
                  {b.tier && (
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${TIER_STYLES[b.tier]}`}>
                      {b.tier}
                    </span>
                  )}
                </div>
                {b.tagline && <div className="text-xs text-white/50 italic mb-3 line-clamp-2">"{b.tagline}"</div>}
                <div className="flex items-end justify-between mt-4">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-white/40">Verified return</div>
                    <div className={`text-2xl font-mono-tab font-black ${(b.ranking_return_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {(b.ranking_return_pct || 0) >= 0 ? "+" : ""}{(b.ranking_return_pct || 0).toFixed(2)}%
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-wider text-white/40">Followers</div>
                    <div className="text-lg font-mono-tab">{b.followers || 0}</div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA footer */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-32 text-center border-t border-white/5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono-tab mb-6">
          <Trophy className="w-3.5 h-3.5" /> Free to start
        </div>
        <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tight mb-6">
          Start paper-trading in <span className="italic text-emerald-400">60 seconds.</span>
        </h2>
        <p className="text-lg text-white/50 max-w-xl mx-auto mb-8">
          $100k virtual cash. Live prices. Real-time alerts. And whenever you're ready — plug in your real broker.
        </p>
        <Button onClick={() => navigate("/register")} data-testid="landing-cta-footer"
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-base px-8 h-14">
          Create your account <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <div className="mt-6 text-xs text-white/40 flex items-center justify-center gap-4">
          <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> $100k virtual cash</span>
          <span>·</span>
          <span>No credit card</span>
          <span>·</span>
          <span>Cancel anytime</span>
        </div>
      </section>

      <footer className="relative z-10 max-w-7xl mx-auto px-6 py-8 text-center text-xs text-white/30 border-t border-white/5">
        © 2026 Aanar · <Link to="/login" className="hover:text-white/60">Sign in</Link>
      </footer>
    </div>
  );
}
